/** @jsx jsx */
import { useRef, useEffect, useCallback, useState } from "react"
import Image from "gatsby-image"
import Slider from "react-slick"
import { Flex, Button, Link, Box, Text, jsx } from "theme-ui"
import { useStaticQuery, graphql } from "gatsby"
import BlockContent from "@sanity/block-content-to-react"

import Bounce from "../../hooks/useBounce"

import useEventListener from "../../hooks/useEventListener"

import { underline } from "../../mixins"
import { isBrowser } from "../../util"
import { CarouselPrevious, CarouselNext } from "../svg"
import "slick-carousel/slick/slick.css"

function Reviews() {
  const slider = useRef(null)
  const slider2 = useRef(null)
  const [slidesToShow, setSlidesToShow] = useState(3)
  const [slidesToShow2, setSlidesToShow2] = useState(5)
  const data = useStaticQuery(graphql`
    query InstagramQuery {
      sanityCustomerReviews(_id: { eq: "customerReviews" }) {
        reviews {
          title
          quote: _rawQuote
        }
      }
      instagramPhotos: allFile(
        filter: { relativeDirectory: { eq: "instagram" } }
      ) {
        edges {
          node {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 99, webpQuality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  const quotes = data.sanityCustomerReviews.reviews

  const images = data.instagramPhotos.edges.map(e => {
    return e.node.childImageSharp.fluid
  })

  useEffect(() => {
    if (isBrowser) {
      if (window.innerWidth <= 768) {
        setSlidesToShow(1)
        setSlidesToShow2(1)
      }
    }
  }, [])

  const settings = {
    arrows: false,
    centerMode: true,
    focusOnSelect: true,
    centerPadding: "60px",
    infinite: true,
    slidesToShow,
    speed: 500,
    swipeToSlide: true,
    controls: false,
  }

  const settings2 = {
    arrows: false,
    centerMode: true,
    focusOnSelect: true,
    infinite: true,
    slidesToShow: slidesToShow2,
    speed: 500,
    swipeToSlide: true,
    controls: false,
  }

  function handler(e) {
    if (e.currentTarget.innerWidth < 768) {
      if (slidesToShow !== 1) {
        setSlidesToShow(1)
        setSlidesToShow2(1)
      }
    } else {
      if (slidesToShow !== 3) {
        setSlidesToShow(3)
        setSlidesToShow2(5)
      }
    }
  }

  useEventListener("resize", handler)

  const styles = {
    container: {
      overflow: "hidden",
      padding: ["0 8px", "0 8px", "0 8px", "0 20px"],
      position: "relative",
      flexDirection: "column",
      background: "linear-gradient(-180deg, #EEA33B 0%, #EFDEC5 100%)",
    },
    sliderContainer: {
      padding: "80px 0 0",
      width: "200vw",
      transform: [
        "translateX(calc(-25% - 8px))",
        null,
        "translateX(calc(-25% - 20px))",
      ],
    },
    sliderContainer2: {
      width: ["calc(100vw + 8px)", null, "calc(100vw + 20px)"],
      transform: ["translateX(-8px)", null, "translateX(-20px)"],
      margin: "55px 0",
    },
    slider: {
      color: "#603813",
      fontFamily: "body",
      fontSize: ["32px", "32px", "32px", "50px"],
      ".slick-track": {
        display: "flex",
        alignItems: "center",
      },
      ".slick-slide": {
        transition: "opacity 1000ms cubic",
        opacity: 0.5,
        padding: ["0 20px", "0 20px", "0 45px"],
      },
      ".slick-center": {
        opacity: 1,
      },
    },
    slider2: {
      ".slick-track": {
        display: "flex",
        alignItems: "center",
      },
      ".slick-slide": {
        transition: "opacity 1000ms cubic",
        padding: ["0 4px", null, "0 10px"],
      },
    },
    clickArea: {
      top: "0px",
      position: "absolute",
      width: "50%",
      height: "100%",
    },
    slide: {
      maxWidth: ["50%", "50%", "690px", null],
      margin: "0 auto",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      textAlign: "center",
      "p:nth-of-type(2)": {
        marginTop: "0.5em",
      },
      svg: {
        maxWidth: "220px",
        height: "auto",
        marginTop: "0.75em",
      },
    },
    controls: {
      margin: "42px auto 0px",
      display: "flex",
      flexDirection: "row",
      button: {
        margin: "5px 5px",
        padding: "5px",
        background: "transparent",
      },
    },
    escape: {
      fontSize: ["32px", "32px", "32px", "50px"],
      textAlign: "center",
      color: "#333333",
      marginTop: ["65px", "65px", "65px", "160px"],
    },
  }

  const handlePrevious = useCallback(() => {
    slider2.current && slider2.current.slickPrev()
  }, [])

  const handleNext = useCallback(() => {
    slider2.current && slider2.current.slickNext()
  }, [])

  const BlockRenderer = props => {
    if (props.node.style === "normal") {
      return (
        <Text as="p" variant="body">
          {props.children}
        </Text>
      )
    }
    return BlockContent.defaultSerializers.types.block(props)
  }

  return (
    <Flex as="section" sx={styles.container}>
      <Box sx={styles.sliderContainer}>
        <Slider ref={slider} {...settings} sx={styles.slider}>
          {quotes.map((q, i) => (
            <Box key={i}>
              <Flex key={i} sx={styles.slide}>
                <BlockContent
                  blocks={q.quote}
                  serializers={{ types: { block: BlockRenderer } }}
                />
              </Flex>
            </Box>
          ))}
        </Slider>
      </Box>
      <Box sx={styles.controls}>
        <Bounce>
          <Button
            aria-label="Previous"
            onClick={() => slider.current.slickPrev()}
          >
            <CarouselPrevious />
          </Button>
        </Bounce>
        <Bounce>
          <Button aria-label="Next" onClick={() => slider.current.slickNext()}>
            <CarouselNext />
          </Button>
        </Bounce>
      </Box>
      <Text as="h2" variant="body" sx={styles.escape}>
        Need an escape?{" "}
        <Link
          sx={{
            ...underline("#333"),
            ":hover": {
              opacity: 0.65,
            },
          }}
          rel="noopener noreferrer"
          target="_blank"
          href="https://www.instagram.com/marcoicecream/"
        >
          @marcoicecream
        </Link>
      </Text>
      <Box sx={styles.sliderContainer2}>
        <Slider ref={slider2} {...settings2} sx={styles.slider2}>
          {images.map((i, idx) => (
            <Box key={i}>
              <Flex key={i}>
                <Image
                  key={idx}
                  sx={{
                    width: "100%",
                    height: "auto",
                  }}
                  fluid={i}
                />
              </Flex>
            </Box>
          ))}
        </Slider>
        <Box
          onClick={() => handlePrevious()}
          sx={{
            ...styles.clickArea,
            left: "0px",
            cursor: `url(/images/prev.png) 0 40, auto`,
          }}
        />
        <Box
          onClick={() => handleNext()}
          sx={{
            ...styles.clickArea,
            right: "0px",
            cursor: `url(/images/next.png) 128 40, auto`,
          }}
        />
      </Box>
    </Flex>
  )
}

export default Reviews
